import React from 'react';
import clsx from 'clsx';
import { ChevronRight, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type TranslationNamespace = string | readonly string[];

export interface BreadcrumbItem {
  id?: string;
  label?: string;
  labelKey?: string;
  href?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  current?: boolean;
}

export interface BreadcrumbProps extends React.HTMLAttributes<HTMLElement> {
  items: BreadcrumbItem[];
  separator?: React.ReactNode;
  translationNs?: TranslationNamespace;
  showHomeIcon?: boolean;
  itemClassName?: string;
  currentItemClassName?: string;
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  items,
  separator = <ChevronRight size={14} aria-hidden="true" />,
  translationNs,
  showHomeIcon = true,
  className,
  itemClassName,
  currentItemClassName,
  ...props
}) => {
  const { t } = useTranslation(translationNs);

  return (
    <nav aria-label="Breadcrumb" className={className} {...props}>
      <ol className="flex flex-wrap items-center gap-2">
        {items.map((item, index) => {
          const resolvedLabel = item.labelKey ? t(item.labelKey) : item.label;
          const isCurrent = item.current ?? index === items.length - 1;
          const leadingIcon = index === 0 && showHomeIcon && !item.icon ? <Home size={14} aria-hidden="true" /> : item.icon;
          const sharedClasses = clsx(
            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-sm font-semibold',
            'border border-black/10 bg-slate-100/90 text-slate-700',
            '[.theme-liquid-glass_&]:border-white/10 [.theme-liquid-glass_&]:bg-white/10 [.theme-liquid-glass_&]:text-white/75',
            !isCurrent && 'transition-colors hover:bg-white hover:text-slate-900 [.theme-liquid-glass_&]:hover:bg-white/15 [.theme-liquid-glass_&]:hover:text-white',
            isCurrent && 'border-black bg-white text-slate-900 [.theme-liquid-glass_&]:border-white/20 [.theme-liquid-glass_&]:bg-white/20 [.theme-liquid-glass_&]:text-white',
            itemClassName,
            isCurrent && currentItemClassName
          );

          const content = (
            <>
              {leadingIcon}
              <span>{resolvedLabel}</span>
            </>
          );

          return (
            <React.Fragment key={item.id || `${resolvedLabel}-${index}`}>
              <li className="flex items-center">
                {isCurrent ? (
                  <span aria-current="page" className={sharedClasses}>
                    {content}
                  </span>
                ) : item.href ? (
                  <a href={item.href} onClick={item.onClick} className={sharedClasses}>
                    {content}
                  </a>
                ) : item.onClick ? (
                  <button type="button" onClick={item.onClick} className={sharedClasses}>
                    {content}
                  </button>
                ) : (
                  <span className={sharedClasses}>{content}</span>
                )}
              </li>
              {index < items.length - 1 && (
                <li aria-hidden="true" className="text-slate-400 [.theme-liquid-glass_&]:text-white/40">
                  {separator}
                </li>
              )}
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
};
